import React, { useEffect } from 'react'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom"
import { AuthRoute, DashboardRoute } from './RouterHelpers'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css';
import _ from 'lodash'
import { APPOINTMENTS, EHR, HOME, DOCUMENTS, getPathNames, VITALS, TASKS } from 'config/helpers/router_helper';
import { motion } from "framer-motion";
import { pageTransition, pageVariants } from 'config/helpers/animation_helper';
import BookingSuccessful from 'screens/appointments/BookingSuccessful';
import { generate_notification } from 'redux/actions/AppActions';
import { useDispatch } from 'react-redux';

const LOGIN_PAGE = React.lazy(() => import("screens/login/LoginPage"));
const ERROR_PAGE = React.lazy(() => import("components/404/NotFound"));

const RootRouter = () => {

  const location = useLocation()
  const dispatch = useDispatch()

  useEffect(() => {
    const messagesSet = new Set()
    const unsubscribe = toast.onChange((messages) => {
      if (messages?.content && !messagesSet.has(messages.content)) {
        messagesSet.add(messages.content)
        dispatch(generate_notification(messages.content))
      }
    });
  
    return () => {
      unsubscribe();
    };
  }, [dispatch]);

  useEffect(() => {
    let paths = getPathNames()
    let path = paths[paths.length - 1] || ''
    document.title = `Enliv Patient | ${_.capitalize(path.replaceAll('_', ' '))}`
  }, [location])

  const hasChildObject = (obj) => {
    let child = []
    Object.keys(obj).map(m => {
      if (typeof obj[m] === 'object' && !obj[m].hasOwnProperty('$$typeof')) {
        Object.keys(obj[m]).map(p => {
          child.push({ component: obj[m][p], parent: m, name: p })
          return true
        })
      }
      return true
    })
    return child
  }

  const AnimationExtendedComponent = (props) => {

    let Module = props.jsx
    let ModuleWrapper = props.wrapper
    let ChildModuleWrapper = props.childwrapper
    return (
      <>
        <ModuleWrapper />
        <motion.div
          key={props.componentKey}
          initial="initial"
          animate="in"
          variants={pageVariants}
          transition={pageTransition}
        >
          {ChildModuleWrapper && <ChildModuleWrapper /> || null}
          <Module />
        </motion.div>
      </>
    )
  }

  const EmptyWrapper = () => {
    return (
      <></>
    )
  }

  const getParentWrapper = (mod, page = false) => {
    if (page === false) {
      if (COMMON_WRAPPERS[mod] && COMMON_WRAPPERS[mod].hasOwnProperty('WRAPPER')) {
        return COMMON_WRAPPERS[mod].WRAPPER
      } else return EmptyWrapper
    } else {
      if (COMMON_WRAPPERS[mod] && COMMON_WRAPPERS[mod][page] && COMMON_WRAPPERS[mod][page].hasOwnProperty('WRAPPER')) {
        return COMMON_WRAPPERS[mod][page].WRAPPER
      } else return EmptyWrapper
    }
  }

  const makePath = (mod, page, sub = false) => {
    let path = ''
    path += _.lowerCase(mod).replaceAll(' ', '_') + '/'
    path += _.lowerCase(page).replaceAll(' ', '_') + '/'
    if (sub) {
      path += _.lowerCase(sub).replaceAll(' ', '_')
    }
    return path
  }

  return (
    <>
      <ToastContainer newestOnTop />
      <Routes>
        {/* {/ auth routes /} */}
        {
          Object.keys(MODULES).map((mod, idx) => {
            return (
              <React.Fragment key={`${mod}_${idx}`}>
                {
                  Object.keys(MODULES[mod]).map((page, pId) => {
                    let childModules = hasChildObject(MODULES[mod][page])
                    let ParentWrapper = getParentWrapper(mod)
                    return (
                      <>
                        {childModules.length === 0 ? <>
                          {
                            Object.keys(MODULES[mod][page]).map((sub, subId) => {
                              let ChildWrapper = getParentWrapper(mod, page)
                              return (
                                <>
                                  {hasChildObject(MODULES[mod][page][sub]).length !== 0 ? <Route
                                    key={`${page}_${idx}_${pId}_${subId}`}
                                    path={makePath(mod, page, sub)}
                                    index
                                    element={<DashboardRoute
                                      component={<AnimationExtendedComponent
                                        componentKey={`${mod}/${page}`}
                                        jsx={MODULES[mod][page][sub]}
                                        wrapper={ParentWrapper}
                                        childwrapper={ChildWrapper}
                                      />}
                                    />}
                                  /> : <></>}
                                </>
                              )
                            })
                          }
                        </> : <></>}
                        <Route
                          key={`${page}_${idx}_${pId}`}
                          path={makePath(mod, page)}
                          index
                          element={<DashboardRoute
                            component={<AnimationExtendedComponent
                              componentKey={`${mod}/${page}`}
                              jsx={MODULES[mod][page]}
                              wrapper={ParentWrapper}
                            />}
                          />}
                        />
                      </>
                    )
                  })
                }
              </React.Fragment>
            )
          })
        }
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" index element={<AuthRoute component={LOGIN_PAGE} />} />
        <Route path="*" element={<ERROR_PAGE />} />
        {/* <Route path="/appointmentStatus/:id" element={<BookingSuccessful/>} /> */}
      </Routes>
    </>
  )
}

const COMMON_WRAPPERS = {
  // [HOME]: {
  //     WRAPPER: React.lazy(() => import('screens/appointments/AppointmentsTabWrapper')),
  //     SOME: {
  //         WRAPPER: React.lazy(() => import('screens/appointments/AppointmentsTabWrapperSub')),
  //     }
  // },
  [APPOINTMENTS]: {
    WRAPPER: React.lazy(() => import('screens/appointments/AppointmentsTabWrapper'))
  },
  [EHR]: {
    WRAPPER: React.lazy(() => import('screens/ehr/EHRTabWrapper'))
  },
  [TASKS]: {
    WRAPPER: React.lazy(() => import('screens/tasks/TasksTabWrapper'))
  }
}

const MODULES = {
  [HOME]: {
    DASHBOARD: React.lazy(() => import('screens/home/Dashboard')),
    SOME: {
      OTHER: React.lazy(() => import('screens/appointments/Appointments')),
      OTHERN: React.lazy(() => import('screens/appointments/Appointments')),
      OTHERP: React.lazy(() => import('screens/appointments/Appointments')),
    }
  },
  [APPOINTMENTS]: {
    // UPCOMING: React.lazy(() => import('screens/appointments/Appointments')),
    RECENT: React.lazy(() => import('screens/appointments/Appointments')),
    COMPLETED: React.lazy(() => import('screens/appointments/Appointments')),
    CANCELLED: React.lazy(() => import('screens/appointments/Appointments')),
    SCHEDULE: React.lazy(() => import('screens/appointments/ScheduleAppointment')),
    DETAILS: React.lazy(() => import('screens/appointments/BookingSuccessful')),
  },
  [EHR]: {
    PERSONAL_INFO: React.lazy(() => import('screens/ehr/PersonalInfo')),
    ENCOUNTERS: React.lazy(() => import('screens/ehr/Encounters')),
    VITALS: React.lazy(() => import('screens/ehr/Vitals')),
    COVERAGE: React.lazy(() => import('screens/ehr/Coverage')),
    ALLERGIES: React.lazy(() => import('screens/ehr/Allergies')),
    PAST_MEDICAL_HISTORY: React.lazy(() => import('screens/ehr/PastMedicalHistory')),
    SOCIAL_HISTORY: React.lazy(() => import('screens/ehr/SocialHistory')),
    FAMILY_MEMBER_HISTORY: React.lazy(() => import('screens/ehr/FamilyMemberHistory')),
    RATING_SCALE: {
      VIEW: React.lazy(() => import('screens/ehr/ratingScale/ViewRatingScale')),
      LIST: React.lazy(() => import('screens/ehr/ratingScale/RatingScale')),
      STATUS: React.lazy(() => import('screens/ehr/ratingScale/RatingScaleStatus')),
    },
    FORMS: {
      VIEW: React.lazy(() => import('screens/ehr/forms/FillAForm'))
    },
    SERVICE_REQUEST: React.lazy(() => import('screens/ehr/ServiceRequest')),
    PHARMACY_MESSAGES: React.lazy(() => import('screens/ehr/PharmacyMessages')),
    IMMUNIZATION: React.lazy(() => import('screens/ehr/Immunization')),

  },
  [DOCUMENTS]: {
    VIEW: React.lazy(() => import('screens/documents/ViewDocuments')),
  },
  [VITALS]: {
    VIEW: React.lazy(() => import('screens/vitals/ViewVitals')),
  },
  [TASKS]: {
    // PENDING: React.lazy(() => import('screens/tasks/task')),
    ALL_TASKS: React.lazy(() => import('screens/tasks/task')),
    REQUESTED: React.lazy(() => import('screens/tasks/task')),
    ACCEPTED: React.lazy(() => import('screens/tasks/task')),
    COMPLETED: React.lazy(() => import('screens/tasks/task')),
    REJECTED: React.lazy(() => import('screens/tasks/task')),
    IN_PROGRESS: React.lazy(() => import('screens/tasks/task')),
    ON_HOLD:React.lazy(() => import('screens/tasks/task')),
    
  }
}

export default RootRouter